import ReviewSource from '@/enums/ReviewSource';
import WidgetDeviceType from '@/enums/WidgetDeviceType';
import WidgetType from '@/enums/WidgetType';
import i18n from '@/lib/i18n';
import { isIframe } from '@/lib/utils';

const MAX_REVIEW_ASK_QUESTION_LENGTH = 200;

function removeEmptyValues(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value != null && value !== '' && value !== undefined
    )
  );
}

const actions = {
  openReviewPasswordDialog({ rootState }, { id, mode }) {
    const params = new URLSearchParams({ mode, locale: i18n.locale });
    return rootState.window.windowService.openFullscreenPopup(
      `/v2/${rootState.brandCode}/reviews/${id}/password?${params.toString()}`
    );
  },
  openReviewEditDialog({ rootState }, { id, password }) {
    const params = new URLSearchParams(rootState.session.sessionParams);
    if (password) params.set('password', password);

    return rootState.window.windowService.openFullscreenPopup(
      `/v2/${rootState.brandCode}/reviews/${id}/edit?${params.toString()}`
    );
  },
  openNewReviewPopup({ rootState }, { subOrderId, productCode } = {}) {
    rootState.api.checkReviewDisabled().then(({ data }) => {
      const { windowService } = rootState.window;
      if (data.disabled) {
        rootState.api.markReviewDisabled();
        windowService.alert(i18n.t('app.new_review_popup_disabled'));
        return;
      }

      const { widget, iframeId, isMobile } = rootState.widget;
      const review_source =
        widget.widget_type === WidgetType.MANAGING_REVIEWS
          ? ReviewSource.BRAND_RESPONSIVE_MY_REVIEW
          : widget.widget_device_type === WidgetDeviceType.PC
          ? ReviewSource.BRAND_PC_PRODUCT
          : ReviewSource.BRAND_MOBILE_PRODUCT_REVIEWS;

      const commonParams = {
        product_code: productCode || rootState.productCode,
        sub_order_id: subOrderId || null,
        review_source,
        from_managing_reviews:
          widget.widget_type === WidgetType.MANAGING_REVIEWS ? true : null
      };

      if (isIframe()) {
        windowService.postMessage(
          'open_new_review_popup',
          removeEmptyValues({
            ...commonParams,
            widget_id: widget.id,
            reload_target_id: iframeId,
            // TODO: 리뷰 작성창 개선 이후 조건 제거하고 항상 iframeId 전달
            source_iframe_id:
              widget.widget_type !== WidgetType.MANAGING_REVIEWS
                ? iframeId
                : null
          })
        );
      } else {
        const params = removeEmptyValues({
          ...commonParams,
          ...rootState.session.sessionParams,
          close_url:
            new URLSearchParams(window.location.search).get('close_url') ||
            window.location.href
        });

        const pathname = rootState.reviewSettings.reviewSettings
          .review_form_enabled
          ? `/v2/${rootState.brandCode}/reviews/new`
          : isMobile
          ? `/${rootState.brandCode}/mobile/reviews/new`
          : `/${rootState.brandCode}/reviews/new`;
        const search = new URLSearchParams(params).toString();

        windowService.redirectTo(`${pathname}?${search}`);
      }
    });
  },
  openBrandNoticesDialog({ rootState }) {
    const isMobile = rootState.widget.isMobile;
    const showMileageNotices = rootState.widget.widget.show_mileage_notices;

    return rootState.window.windowService.openFullscreenPopup(
      `/v2/${rootState.brandCode}/brand_notices?mobile=${isMobile}&show_mileage_notices=${showMileageNotices}&locale=${i18n.locale}`
    );
  },
  openReviewPolicyDialog({ rootState }) {
    const isMobile = rootState.widget.isMobile;

    return rootState.window.windowService.openFullscreenPopup(
      `/v2/${rootState.brandCode}/review_policy?mobile=${isMobile}&locale=${i18n.locale}`
    );
  },
  openSizeRecommendationPopup({ rootState }) {
    const { isMobile, widget } = rootState.widget;
    const params = removeEmptyValues({
      mobile: isMobile,
      widget_id: widget?.id,
      product_code: rootState.productCode,
      ...rootState.session.sessionParams
    });
    const search = new URLSearchParams(params).toString();
    return rootState.window.windowService.openFullscreenPopup(
      `/v2/${rootState.brandCode}/add_on/size_recommendation/popup?${search}`
    );
  },
  openSpamReportDialog(
    { rootState },
    {
      spamReportableId,
      spamReportableType,
      spamReportableBrandUserId,
      articleBlocked,
      targetElement
    } = {}
  ) {
    const params = removeEmptyValues({
      spam_reportable_id: spamReportableId,
      spam_reportable_type: spamReportableType,
      spam_reportable_brand_user_id: spamReportableBrandUserId,
      article_blocked: articleBlocked,
      selected_spam_type_tab: 'article',
      locale: i18n.locale,
      ...rootState.session.sessionParams
    });
    const search = new URLSearchParams(params).toString();
    return rootState.window.windowService.openFullscreenPopup(
      `/v2/${rootState.brandCode}/spam_reports/new?${search}`,
      targetElement
    );
  },
  openReviewAskDialog(
    { rootState },
    { question, presets, targetElement } = {}
  ) {
    const params = removeEmptyValues({
      product_id: rootState.review.productId,
      question: question && question.slice(0, MAX_REVIEW_ASK_QUESTION_LENGTH),
      locale: i18n.locale,
      main_color: rootState.reviewSettings.reviewSettings.main_color,
      presets: presets && presets.length ? JSON.stringify(presets) : undefined,
      page_view_id: rootState.pageViewId
    });
    const search = new URLSearchParams(params).toString();
    return rootState.window.windowService.openFullscreenPopup(
      `/v2/${rootState.brandCode}/review_ask?${search}`,
      targetElement
    );
  }
};

export default { namespaced: true, actions };
